import React, { useState } from 'react';
import './VisionMissionSection.css';
import './VisionMissionSection.css';
import './VisionMissionSection.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const VisionMissionSection = () => {
    const [ref, inView] = useInView({
      triggerOnce: true,
      threshold: 0.1,
    });
  const [visionModalOpen, setVisionModalOpen] = useState(false);
  const [thoughtProcessModalOpen, setThoughtProcessModalOpen] = useState(false);
  const [missionModalOpen, setMissionModalOpen] = useState(false);

  const openVisionModal = () => setVisionModalOpen(true);
  const openThoughtProcessModal = () => setThoughtProcessModalOpen(true);
  const openMissionModal = () => setMissionModalOpen(true);
  const closeModal = () => {
    setVisionModalOpen(false);
    setThoughtProcessModalOpen(false);
    setMissionModalOpen(false);
  };

  // Handle ESC key press to close modal
  React.useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, []);

  return (
    <>
      {/* Vision, Thought Process & Mission Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 floating-svg-element">
          <svg viewBox="0 0 1000 400" className="w-full h-full">
            <defs>
              <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            <circle cx="200" cy="100" r="2" fill="currentColor"/>
            <circle cx="400" cy="150" r="2" fill="currentColor"/>
            <circle cx="600" cy="80" r="2" fill="currentColor"/>
            <circle cx="800" cy="120" r="2" fill="currentColor"/>
            <line x1="200" y1="100" x2="400" y2="150" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/>
            <line x1="400" y1="150" x2="600" y2="80" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/>
            <line x1="600" y1="80" x2="800" y2="120" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="reveal-text inline-flex items-center space-x-3 bg-white/80 backdrop-blur-sm text-blue-600 rounded-full px-6 py-3 mb-8 shadow-lg">
              <span className="text-lg">🌟</span>
              <span className="font-semibold tracking-wide">OUR FOUNDATION</span>
            </div>

          <motion.h2
            ref={ref}
            className="display-text text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            Driven by Purpose,
            <span className="text-gradient-political block mt-2">Guided by Vision</span>
          </motion.h2>


            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed reveal-text">
              Discover the philosophy and mission that drives India's leading political-tech ecosystem,
              inspired by <span className="text-gradient-political">Mr. Varun Shivpuriya's</span> transformative vision.
            </p>
          </div>

          {/* Three Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 hidden md:grid">
            {/* Vision Card */}
            <div className="reveal-card group cursor-pointer" onClick={openVisionModal}>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden z-10">
                {/* Card Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="10" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="3" fill="currentColor"/>
                  </svg>
                </div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-3xl">👁️‍🗨️</span>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                    Our Vision
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Building India's most trusted political-tech ecosystem where strategy meets integrity and design strengthens democracy.
                  </p>

                  {/* Key Points */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Empower Next-Gen Leaders</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Build Lasting Legacies</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Strengthen Democracy</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-2 transition-transform duration-300">
                    <span>Explore Vision</span>
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Thought Process Card */}
            <div className="reveal-card group cursor-pointer" onClick={openThoughtProcessModal}>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden z-10">
                {/* Card Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="20" cy="20" r="2" fill="currentColor"/>
                    <circle cx="50" cy="30" r="2" fill="currentColor"/>
                    <circle cx="80" cy="20" r="2" fill="currentColor"/>
                    <circle cx="30" cy="50" r="2" fill="currentColor"/>
                    <circle cx="70" cy="60" r="2" fill="currentColor"/>
                    <circle cx="40" cy="80" r="2" fill="currentColor"/>
                    <line x1="20" y1="20" x2="50" y2="30" stroke="currentColor" strokeWidth="0.5"/>
                    <line x1="50" y1="30" x2="80" y2="20" stroke="currentColor" strokeWidth="0.5"/>
                    <line x1="30" y1="50" x2="70" y2="60" stroke="currentColor" strokeWidth="0.5"/>
                  </svg>
                </div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-3xl">💡</span>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors">
                    Thought Process
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Mr. Varun Shivpuriya's strategic methodology combines analytical thinking with creative innovation for transformative results.
                  </p>

                  {/* Key Points */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Data-Driven Decisions</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Strategic Innovation</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Ethical Leadership</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center text-purple-600 font-medium group-hover:translate-x-2 transition-transform duration-300">
                    <span>Discover Philosophy</span>
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Card */}
            <div className="reveal-card group cursor-pointer" onClick={openMissionModal}>
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden z-10">
                {/* Card Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="10" fill="none" stroke="currentColor" strokeWidth="1"/>
                    <circle cx="50" cy="50" r="3" fill="currentColor"/>
                    <line x1="50" y1="10" x2="50" y2="20" stroke="currentColor" strokeWidth="1"/>
                    <line x1="50" y1="80" x2="50" y2="90" stroke="currentColor" strokeWidth="1"/>
                    <line x1="10" y1="50" x2="20" y2="50" stroke="currentColor" strokeWidth="1"/>
                    <line x1="80" y1="50" x2="90" y2="50" stroke="currentColor" strokeWidth="1"/>
                  </svg>
                </div>

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-3xl">🎯</span>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors">
                    Our Mission
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Democratizing political communication through ethical practices, innovative technology, and trust-building initiatives.
                  </p>

                  {/* Key Points */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Ethical Practices</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Trust Building</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Democratic Values</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center text-green-600 font-medium group-hover:translate-x-2 transition-transform duration-300">
                    <span>Learn Mission</span>
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA Section */}
          <div className="mt-20 text-center">
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10 floating-svg-element">
                <svg viewBox="0 0 400 200" className="w-full h-full">
                  <circle cx="100" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="1" className="floating-svg-element"/>
                                                                        <circle cx="300" cy="150" r="40" fill="none" stroke="currentColor" strokeWidth="1" className="floating-svg-element"/>
                                    <circle cx="200" cy="100" r="20" fill="none" stroke="currentColor" strokeWidth="1" className="floating-svg-element"/>
                  <line x1="100" y1="50" x2="200" y2="100" stroke="currentColor" strokeWidth="0.5" className="floating-svg-element"/>
                  <line x1="200" y1="100" x2="300" y2="150" stroke="currentColor" strokeWidth="0.5" className="floating-svg-element"/>
                </svg>
              </div>

              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Ready to Transform Your Political Journey?
                </h3>
                <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                  Connect with our strategy experts and discover how our vision-driven approach can elevate your campaign to new heights.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a
                    href="tel:+917725898440"
                    className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg flex items-center space-x-2"
                  >
                    <span>📞</span>
                    <span>: +91 77258 98440</span>
                  </a>
                  <a
                    href="mailto:contact@jetdigital.in"
                    className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center space-x-2"
                  >
                    <span>✉️</span>
                    <span>contact@jetdigital.in</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Modal */}
      {visionModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-6 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">👁️‍🗨️</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Our Vision</h2>
                    <p className="text-blue-100">Building India's Political-Tech Future</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white hover:text-blue-200 text-3xl font-light"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Vision Statement</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    To build India's most trusted and innovative political-tech ecosystem where strategic excellence meets unwavering integrity,
                    empowering leaders to create lasting positive change and strengthen democratic values across the nation.
                  </p>

                  <h4 className="font-semibold mb-3 text-gray-900">Core Vision Elements:</h4>
                  <ul className="space-y-3 text-gray-600 mb-6">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">✓</span>
                      <span>Empower next-generation political leaders with cutting-edge technology</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">✓</span>
                      <span>Build lasting legacies through strategic campaign excellence</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">✓</span>
                      <span>Strengthen democratic institutions through ethical practices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">✓</span>
                      <span>Foster transparent and accountable governance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2">✓</span>
                      <span>Create sustainable political communication frameworks</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Future Goals</h3>
                  <div className="space-y-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-blue-900 mb-2">2025 Vision</h5>
                      <p className="text-blue-700 text-sm">Establish presence in 15+ states with 500+ successful campaigns</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-purple-900 mb-2">2027 Vision</h5>
                      <p className="text-purple-700 text-sm">Become India's leading political-tech platform with AI-driven solutions</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-green-900 mb-2">2030 Vision</h5>
                      <p className="text-green-700 text-sm">Global expansion with democratic technology solutions worldwide</p>
                    </div>
                  </div>

                  <h4 className="font-semibold mb-3 text-gray-900">Contact Our Vision Team:</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Bhopal (Head Office):</strong> 304, Vivek Tower, Bitthan Market, E5 Arera Colony, Commercial Zone, Bhopal - 462016</p>
                    <p><strong>Indore:</strong> 23, PU4 Commercial Zone, MR 9, Indore - 452010</p>
                    <p><strong>Kalapipal:</strong> 544, Ward 13, New Bus Station, Kalapipal, Distt – Shajapur (MP) - 465337</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+917725898440"
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>📞</span>
                  <span>Contact Us Now</span>
                </a>
                <a
                  href="mailto:contact@jetdigital.in"
                  className="border border-blue-600 text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>✉️</span>
                  <span>Email Our Team</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Thought Process Modal */}
      {thoughtProcessModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-700 text-white p-6 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">💡</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Thought Process</h2>
                    <p className="text-purple-100">Mr. Varun Shivpuriya's Strategic Philosophy</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white hover:text-purple-200 text-3xl font-light"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Strategic Philosophy</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Mr. Varun Shivpuriya's thought process combines analytical rigor with creative innovation,
                    ensuring every campaign strategy is both data-driven and emotionally resonant. His methodology
                    focuses on understanding the deeper currents of political sentiment while maintaining ethical standards.
                  </p>

                  <h4 className="font-semibold mb-3 text-gray-900">Core Principles:</h4>
                  <ul className="space-y-3 text-gray-600 mb-6">
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">✓</span>
                      <span>Data-driven decision making with human insight</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">✓</span>
                      <span>Strategic innovation through creative problem-solving</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">✓</span>
                      <span>Ethical leadership in all campaign activities</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">✓</span>
                      <span>Long-term vision with short-term tactical excellence</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-500 mr-2">✓</span>
                      <span>Collaborative approach with stakeholder engagement</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Analytical Methodology</h3>
                  <div className="space-y-4 mb-6">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-purple-900 mb-2">Research & Analysis</h5>
                      <p className="text-purple-700 text-sm">Deep constituency research, voter behavior analysis, and sentiment mapping</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-blue-900 mb-2">Strategic Planning</h5>
                      <p className="text-blue-700 text-sm">Multi-layered campaign strategies with contingency planning and risk assessment</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-green-900 mb-2">Execution Excellence</h5>
                      <p className="text-green-700 text-sm">Precise implementation with real-time monitoring and adaptive optimization</p>
                    </div>
                  </div>

                  <h4 className="font-semibold mb-3 text-gray-900">Innovation Areas:</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>• AI-powered voter sentiment analysis</p>
                    <p>• Micro-targeting with ethical boundaries</p>
                    <p>• Cross-platform digital integration</p>
                    <p>• Real-time campaign optimization</p>
                    <p>• Predictive analytics for strategic planning</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+917725898440"
                  className="bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>📞</span>
                  <span>Discuss Strategy</span>
                </a>
                <a
                  href="mailto:contact@jetdigital.in"
                  className="border border-purple-600 text-purple-600 px-6 py-3 rounded-xl font-semibold hover:bg-purple-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>✉️</span>
                  <span>Connect with Varun</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mission Modal */}
      {missionModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-700 text-white p-6 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Our Mission</h2>
                    <p className="text-green-100">Democratizing Political Communication</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white hover:text-green-200 text-3xl font-light"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Mission Statement</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    To democratize political communication through ethical practices, innovative technology, and trust-building initiatives
                    that empower leaders to connect authentically with their constituents while strengthening democratic institutions
                    and fostering transparent governance across India.
                  </p>

                  <h4 className="font-semibold mb-3 text-gray-900">Core Mission Elements:</h4>
                  <ul className="space-y-3 text-gray-600 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>Ethical practices in all political communications</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>Trust-building between leaders and constituents</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>Democratic values promotion and protection</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>Transparent and accountable campaign practices</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span>Inclusive political participation for all citizens</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Impact Areas</h3>
                  <div className="space-y-4 mb-6">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-green-900 mb-2">Democratic Communication</h5>
                      <p className="text-green-700 text-sm">Facilitating authentic dialogue between political leaders and citizens</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-blue-900 mb-2">Ethical Technology</h5>
                      <p className="text-blue-700 text-sm">Implementing responsible AI and digital tools for political engagement</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-purple-900 mb-2">Trust Building</h5>
                      <p className="text-purple-700 text-sm">Creating sustainable relationships based on transparency and accountability</p>
                    </div>
                  </div>

                  <h4 className="font-semibold mb-3 text-gray-900">Our Offices:</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Bhopal (Head Office):</strong> 304, Vivek Tower, Bitthan Market, E5 Arera Colony, Commercial Zone, Bhopal - 462016</p>
                    <p><strong>Indore:</strong> 23, PU4 Commercial Zone, MR 9, Indore - 452010</p>
                    <p><strong>Kalapipal:</strong> 544, Ward 13, New Bus Station, Kalapipal, Distt – Shajapur (MP) - 465337</p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+917725898440"
                  className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>📞</span>
                  <span>Join Our Mission</span>
                </a>
                <a
                  href="mailto:contact@jetdigital.in"
                  className="border border-green-600 text-green-600 px-6 py-3 rounded-xl font-semibold hover:bg-green-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>✉️</span>
                  <span>Partner With Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VisionMissionSection;
